import { type VariantProps, cva } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import type * as React from 'react';

import { cn } from '@/lib/utils';

// Status tags. The tones carry CEISA states: info (in progress), ok (accepted), warn (needs action),
// err (rejected), ai (Jarvis suggestions, the violet plane). Tone text uses the *-ink tokens, which
// pass AA on the tint in light mode, where the tone itself does not.
const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-tag border px-1.5 py-0.5 font-semibold text-xs leading-4 transition-[color,box-shadow] focus-visible:ring-[3px] focus-visible:ring-label/40 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        neutral: 'border-line-strong bg-surface-2 text-fg-2',
        info: 'border-label/25 bg-label/10 text-info-ink',
        ok: 'border-ok/25 bg-ok/10 text-ok-ink',
        warn: 'border-warn/30 bg-warn/12 text-warn-ink',
        err: 'border-err/25 bg-err/10 text-err-ink',
        ai: 'border-ai/25 bg-ai/10 text-ai-ink',
        outline: 'border-line-control text-fg',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  }
);

/** A Badge tone, for maps of status → tone: `const tone: BadgeVariant = 'ok'`. */
type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

function Badge({
  className,
  variant = 'neutral',
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp data-slot="badge" data-variant={variant} className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants, type BadgeVariant };
